import { isEmpty } from 'lodash';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { Checkbox, useQueryFetch } from 'sonata-react-components';
import { DeviceCarrier, DeviceCarrierApiResponse } from 'sonata-static-storage/lib/common/common.types';

import { Question } from '../Question/Question';
import { CarriersList } from './CarriersList';
import { CarriersSelection } from './CarriersSelection';

import './carriers.styles.scss';

type Props = {
  campaignCountry: string;
  carriers: DeviceCarrier[];
  countriesForSelector?: string[];
  getDeviceCarriers: () => Promise<Record<string, DeviceCarrierApiResponse>>;
  id: string;
  omitQuestion?: boolean;
  onCarriersChanged: (selectedDeviceMakeModels: DeviceCarrier[]) => void;
  onUnknownIncludedToggled: (unknownIncluded: boolean) => void;
  unknownIncluded: boolean;
  unknownIncludedPermission: boolean;
};

type State = {
  deviceCarriersEnabled: boolean;
};

export const Carriers: React.FC<Props> = ({
  campaignCountry,
  carriers,
  countriesForSelector,
  getDeviceCarriers,
  id,
  omitQuestion = false,
  onCarriersChanged,
  onUnknownIncludedToggled,
  unknownIncluded,
  unknownIncludedPermission
}) => {
  const { formatMessage } = useIntl();

  const [{ deviceCarriersEnabled }, setState] = useState<State>({
    deviceCarriersEnabled: Object.keys(carriers).length > 0 || omitQuestion
  });

  const handlePacingQuestionChanged = (newStatus: boolean) => {
    onCarriersChanged?.([]);
    setState(prevState => ({
      ...prevState,
      deviceCarriersEnabled: newStatus
    }));
  };

  const handleCarriersOnChanged = (newCarriers: DeviceCarrier[]) => {
    onCarriersChanged?.(newCarriers);
  };

  const handleUnknownIncludedToggled = (_id: string, checked: boolean) => {
    onUnknownIncludedToggled(checked);
  };

  const handleCarrierRemoved = (removedCarrierId: string) => {
    onCarriersChanged?.(
      carriers.reduce((newCarriers: DeviceCarrier[], entryCarriers) => {
        const entryFilteredCarriers = Object.entries(entryCarriers).reduce(
          (filteredCarriers, [carrierId, carrierName]) => ({
            ...filteredCarriers,
            ...(carrierId !== removedCarrierId && { [carrierId]: carrierName })
          }),
          {}
        );
        return newCarriers.concat(isEmpty(entryFilteredCarriers) ? [] : entryFilteredCarriers);
      }, [])
    );
  };

  const { data = {}, status } = useQueryFetch(`${id}-makeModels`, getDeviceCarriers, {
    cacheTime: 0,
    enabled: deviceCarriersEnabled,
    retry: 0,
    staleTime: 0
  });

  return (
    <div className="deviceCarriersContainer" id={`${id}_make_models_box_container`}>
      {!omitQuestion && (
        <Question
          deviceMakeModelsEnabled={deviceCarriersEnabled}
          disabledText={formatMessage({
            defaultMessage: 'Show ads across all device makers',
            id: 'targetingDeviceCarriers.question.disabled'
          })}
          enabledText={formatMessage({
            defaultMessage: 'Target specific device maker',
            id: 'targetingDeviceCarriers.question.enabled'
          })}
          handlePacingQuestionChanged={handlePacingQuestionChanged}
          id={`${id}_carriers_targeting_question`}
        />
      )}
      {deviceCarriersEnabled && status === 'success' && (
        <CarriersSelection
          availableCarriers={data}
          campaignCountry={campaignCountry}
          carriers={carriers}
          countriesForSelector={countriesForSelector}
          id={`${id}_carriers_selector`}
          onChangeCarriers={handleCarriersOnChanged}
        />
      )}
      {!isEmpty(carriers) && status === 'success' && (
        <CarriersList availableCarriers={data} carriers={carriers} onRemoveCarrier={handleCarrierRemoved} />
      )}
      {unknownIncludedPermission && Object.keys(carriers).length > 0 && (
        <div className="deviceCarriersContainer__unknownIncluded">
          <Checkbox
            defaultActive={unknownIncluded}
            id={`${id}_unknownIncluded`}
            labelText="Unknown Included"
            onClickAction={handleUnknownIncludedToggled}
          />
        </div>
      )}
    </div>
  );
};
